import React from 'react';
import { NotificationConfig, NotificationType, Theme } from '@routific/hive';
import i18n from '../../i18n';

/**
 * A location change the planner made on a published route has been reverted, because the driver's own work
 * arrived afterwards and proved the point had already gone by: they had begun, for a start location, or
 * finished, for an end one.
 *
 * The copy names both locations rather than the one that went, because the DTO carries only the route — and
 * because a driver who was offline all day syncs both departures in a single batch, which reverts both under
 * one notification. It stays vague about where the location went for the same reason it must cover a route
 * published without one at all: that staged location is removed outright rather than moved back.
 *
 * A warning rather than a success: the edit was accepted at the time, so without this the planner believes the
 * driver is heading somewhere they are not.
 */
export const stagedEditDiscardedNotification = (routeUuid: string, routeName?: string | null): NotificationConfig => ({
  id: `route-staged-edit-discarded-${routeUuid}`,
  type: NotificationType.WARNING,
  // Hive drops a caller's `description`, overwriting it with the optional link button, so the two-line shape
  // has to ride on `message` as a node. Same approach as the Shopify connection errors.
  message: (
    <>
      <div>{i18n.t('notifications:editRoute.stagedEditDiscarded.title')}</div>
      <div style={{ fontSize: 12, lineHeight: '16px', fontWeight: 400, marginTop: Theme.Spacing.xsSpacing }}>
        {routeName
          ? i18n.t('notifications:editRoute.stagedEditDiscarded.named', { routeName })
          : i18n.t('notifications:editRoute.stagedEditDiscarded.message')}
      </div>
    </>
  ),
  dataCy: 'route-staged-edit-discarded-notification',
});
